/**
 * Axios client used by every API call in the app.
 *
 * Security model:
 *   * Access token is held in a JS module variable. XSS would still see
 *     it for the page lifetime, but a hard reload signs the user out
 *     unless the refresh-token cookie below validates them again.
 *   * Refresh token lives in an httpOnly + SameSite=Lax cookie set by
 *     the Django auth views, scoped to /api/auth/. JS cannot read it,
 *     XSS cannot exfiltrate it, and the browser attaches it
 *     automatically to refresh requests (withCredentials: true).
 *
 * On a 401 from any non-auth endpoint we transparently call
 * /auth/token/refresh/ once. If THAT 401s we drop the access token and
 * signal AuthContext to redirect to /login.
 */

import axios from "axios";

let accessToken = null;
let onAuthCleared = null;

export function setAccessToken(token) {
  accessToken = token || null;
}
export function getAccessToken() {
  return accessToken;
}
export function clearAccessToken() {
  accessToken = null;
}
export function onAuthLost(cb) {
  onAuthCleared = cb;
}

const api = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
  // Send the refresh-token cookie on every API request. Same-origin in
  // prod (both behind seogeeks.com.ua) and same-origin in dev (Vite
  // proxies /api → :8000), so no CORS withCredentials gymnastics.
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

let refreshPromise = null;
export async function refreshAccessToken() {
  if (!refreshPromise) {
    // No body — the refresh token rides on the cookie.
    refreshPromise = axios
      .post("/api/auth/token/refresh/", {}, { withCredentials: true })
      .then((res) => {
        setAccessToken(res.data.access);
        return res.data.access;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes("/auth/token/refresh/") &&
      !original.url?.includes("/auth/login/")
    ) {
      original._retry = true;
      try {
        const newAccess = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch {
        clearAccessToken();
        if (onAuthCleared) onAuthCleared();
      }
    }
    return Promise.reject(error);
  },
);

export default api;
